/**
 * Keeping the screen awake while a terminal is being watched.
 *
 * WHAT GOES WRONG WITHOUT IT
 * --------------------------
 * A phone dims and locks after thirty seconds of not being touched, and
 * watching output is not touching anything. What follows is not merely a dark
 * screen: the page is frozen, its timers stop, and on iOS every socket it held
 * is closed without a close event reaching the page. So the operator unlocks to
 * a console that looks live and is not — which is the one failure this whole
 * product is built to avoid, arriving through the display settings.
 *
 * The reconnect paths handle the coming back (`pageshow`, `visibilitychange`,
 * `online`, all wired in `useSystemStatus` and `TerminalPane`). This handles not
 * going away in the first place, which is the better outcome when somebody is
 * holding the phone to read a build scroll past.
 *
 * WHY IT IS A CHOICE AND NOT A DEFAULT
 * ------------------------------------
 * Holding the screen on drains a battery, and an operator who put the phone
 * down did not ask for that. So it is a control, off unless asked for, and the
 * request is dropped the moment the page is hidden anyway — the browser revokes
 * the lock itself on hide, and re-taking it on return is what makes the choice
 * survive a lock screen instead of being silently lost the first time.
 *
 * `navigator.wakeLock` exists in Chromium and in Safari since 16.4. Where it
 * does not, `supported` is false and the caller draws nothing: a switch that
 * cannot do the thing it names is worse than an absence.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

/** Not in the DOM lib in every TypeScript version this repo has built under. */
interface WakeLockSentinelLike {
  released: boolean
  release: () => Promise<void>
  addEventListener: (type: 'release', listener: () => void) => void
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>
}

function api(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null
  const nav = navigator as Navigator & { wakeLock?: WakeLockLike }
  return nav.wakeLock ?? null
}

export interface WakeLock {
  /** Whether this browser can hold one at all. */
  supported: boolean
  /** Whether the operator has asked for it — not whether one is held right now.
   *  Those differ every time the page is hidden, and the *intent* is what a
   *  control should show: a switch that flicked itself off on every lock screen
   *  would be reporting the browser's bookkeeping rather than the choice. */
  wanted: boolean
  toggle: () => void
}

export function useWakeLock(): WakeLock {
  const [wanted, setWanted] = useState(false)
  const sentinel = useRef<WakeLockSentinelLike | null>(null)
  const supported = api() !== null

  useEffect(() => {
    if (!wanted || !supported) return
    let cancelled = false

    const acquire = async () => {
      // Only from a visible page. The specification requires it, and a request
      // from a hidden one rejects — which, without this guard, is an unhandled
      // rejection on every return from the background.
      if (document.visibilityState !== 'visible') return
      if (sentinel.current && !sentinel.current.released) return
      try {
        const lock = await api()!.request('screen')
        if (cancelled) {
          void lock.release()
          return
        }
        sentinel.current = lock
        // The browser releases it on hide, on a battery-saver threshold, and on
        // some devices for reasons it does not explain. Listening rather than
        // assuming is what lets the visibility handler below re-take it.
        lock.addEventListener('release', () => {
          if (sentinel.current === lock) sentinel.current = null
        })
      } catch {
        // A refused lock is not an error worth a screen. The console works, the
        // phone sleeps, and the reconnect paths pick it up on the way back.
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') void acquire()
    }

    void acquire()
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      const held = sentinel.current
      sentinel.current = null
      if (held && !held.released) void held.release()
    }
  }, [wanted, supported])

  const toggle = useCallback(() => setWanted((on) => !on), [])
  return { supported, wanted, toggle }
}
